// =====================================================================
// src/screens/v2/SharePostSheet.jsx
//
// Phase 2.5 / C10c — Bottom sheet for sharing a wall post.
//
// Three actions:
//   1. "Share in Aangan chat" — expands into a chat picker. Tap a chat
//      → sendChatMessage with the post URL as text → toast + dismiss.
//      Reuses V1's chat infrastructure (subscribeToUserChats,
//      sendChatMessage, makeChatId).
//   2. "Share outside Aangan" — invokes navigator.share with the URL
//      (system share sheet: WhatsApp, Instagram, etc). Falls back to
//      clipboard if navigator.share isn't available.
//   3. "Copy link" — explicit copy button (always shown as a last resort).
//
// The URL format is /biz/CODE/post/POSTID — the App.jsx URL parser
// resolves this to the post detail screen for both authenticated and
// unauthenticated users (with the auth/onboarding flow for the latter).
//
// Props:
//   post          the post object (must have .id and .businessId)
//   currentUser   for chat picker — chats are user-scoped
//   onClose       () => void
//   onSetToast    (string) => void
// =====================================================================

import React, { useEffect, useMemo, useState } from 'react';
import { X, Send, ExternalLink, Link as LinkIcon, MessageSquare } from 'lucide-react';
import { doc as fsDoc, getDoc } from 'firebase/firestore';
import { db } from '../../lib/firebase';
import { subscribeToUserChats, sendChatMessage } from '../../lib/db';
import { buildShareUrl } from '../../lib/shareBase';

export default function SharePostSheet({ post, currentUser, onClose, onSetToast }) {
  const [mode, setMode] = useState('menu'); // 'menu' | 'chat-picker'
  const [chats, setChats] = useState(null);
  const [chatPartners, setChatPartners] = useState({}); // chatId -> partner user obj
  const [sendingTo, setSendingTo] = useState(null); // chatId currently being sent to

  // Build the shareable URL from the PUBLIC origin.
  //
  // P1: this used window.location.origin, with a comment claiming it worked
  // "in both dev and prod". It does not work where it matters: on Capacitor
  // native the origin is http://localhost, so a post shared from the Android
  // app hands the recipient a dead link. Same bug App.jsx fixed for V1 with
  // AANGAN_SHARE_BASE.
  //
  // The path /biz/{bizId}/post/{postId} is handled by App.jsx's URL parser.
  const shareUrl = useMemo(
    () => buildShareUrl(`/biz/${post.businessId}/post/${post.id}`),
    [post],
  );

  // Body scroll lock while open
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, []);

  // ---- Chat list subscription (only when picker is open) ----
  useEffect(() => {
    if (mode !== 'chat-picker' || !currentUser?.id) return;
    const unsub = subscribeToUserChats(currentUser.id, (list) => {
      setChats(list || []);
    });
    return unsub;
  }, [mode, currentUser?.id]);

  // ---- Hydrate chat partner names ----
  // V1's chats only store participant uids; we need to fetch names for
  // display. One-time lookup per chat partner.
  useEffect(() => {
    if (!chats || !currentUser?.id) return;
    const partnerIds = chats
      .map(c => (c.participants || []).find(uid => uid !== currentUser.id))
      .filter(Boolean);
    const missing = partnerIds.filter(uid => !chatPartners[uid]);
    if (missing.length === 0) return;

    let cancelled = false;
    (async () => {
      const fetched = {};
      await Promise.all(
        missing.map(async (uid) => {
          try {
            const snap = await getDoc(fsDoc(db, 'users', uid));
            if (snap.exists()) fetched[uid] = { id: snap.id, ...snap.data() };
          } catch (e) {
            console.warn('[SharePostSheet] partner fetch failed:', uid, e);
          }
        })
      );
      if (!cancelled && Object.keys(fetched).length > 0) {
        setChatPartners(prev => ({ ...prev, ...fetched }));
      }
    })();
    return () => { cancelled = true; };
  }, [chats, currentUser?.id, chatPartners]);

  // ---- External share ----
  async function handleShareOutside() {
    if (!shareUrl) return;
    try {
      if (typeof navigator !== 'undefined' && navigator.share) {
        await navigator.share({ url: shareUrl, title: 'A post on Aangan' });
        // No toast — the system sheet itself is the confirmation
      } else if (typeof navigator !== 'undefined' && navigator.clipboard) {
        await navigator.clipboard.writeText(shareUrl);
        onSetToast?.('Link copied to clipboard');
      } else {
        onSetToast?.('Could not share — please copy the link manually');
      }
      onClose?.();
    } catch (err) {
      // User cancellation of navigator.share throws AbortError — silent.
      if (err?.name !== 'AbortError') {
        console.warn('[SharePostSheet] share failed:', err);
        onSetToast?.('Share failed');
      }
    }
  }

  // ---- Copy link ----
  async function handleCopyLink() {
    if (!shareUrl) return;
    try {
      if (navigator?.clipboard) {
        await navigator.clipboard.writeText(shareUrl);
        onSetToast?.('Link copied');
      } else {
        // Legacy fallback
        const ta = document.createElement('textarea');
        ta.value = shareUrl;
        document.body.appendChild(ta);
        ta.select();
        document.execCommand('copy');
        document.body.removeChild(ta);
        onSetToast?.('Link copied');
      }
      onClose?.();
    } catch (err) {
      console.warn('[SharePostSheet] copy failed:', err);
      onSetToast?.('Could not copy link');
    }
  }

  // ---- Send to a specific chat ----
  async function handleSendToChat(chat) {
    if (!chat || sendingTo) return;
    const partnerId = (chat.participants || []).find(uid => uid !== currentUser.id);
    if (!partnerId) return;
    setSendingTo(chat.id);
    try {
      await sendChatMessage({
        chatId: chat.id,
        fromId: currentUser.id,
        toId: partnerId,
        text: shareUrl,
      });
      onSetToast?.('Sent');
      onClose?.();
    } catch (err) {
      console.error('[SharePostSheet] sendChatMessage failed:', err);
      onSetToast?.(`Couldn't send: ${err.code || err.message || 'try again'}`);
      setSendingTo(null);
    }
  }

  // ---- Render ----
  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0,0,0,0.5)',
        zIndex: 70,
        display: 'flex',
        alignItems: 'flex-end',
        justifyContent: 'center',
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: '100%',
          maxWidth: 480,
          maxHeight: '80vh',
          background: 'var(--bg)',
          borderTopLeftRadius: 20,
          borderTopRightRadius: 20,
          display: 'flex',
          flexDirection: 'column',
          paddingBottom: 'env(safe-area-inset-bottom)',
        }}
      >
        {/* Drag handle */}
        <div className="w-12 h-1 rounded-full mx-auto my-3 shrink-0"
             style={{ background: 'var(--border)' }} />

        {/* Header */}
        <div className="flex items-center justify-between px-4 pb-3 shrink-0">
          <div className="text-[15px] font-medium" style={{ color: 'var(--ink)' }}>
            {mode === 'chat-picker' ? 'Pick a chat' : 'Share this post'}
          </div>
          <button onClick={onClose}
                  className="w-8 h-8 rounded-full flex items-center justify-center active:scale-95"
                  aria-label="Close share sheet">
            <X size={18} style={{ color: 'var(--ink-soft)' }} />
          </button>
        </div>

        {/* Body */}
        {mode === 'menu' && (
          <div className="px-4 pb-5">
            <button onClick={() => setMode('chat-picker')}
                    className="w-full flex items-center gap-3 p-3 rounded-2xl active:scale-[0.99] transition-transform mb-2"
                    style={{ background: 'var(--surface)', border: '1px solid var(--border)' }}>
              <div className="w-10 h-10 rounded-full flex items-center justify-center shrink-0"
                   style={{ background: 'var(--accent-soft)' }}>
                <MessageSquare size={18} style={{ color: 'var(--accent)' }} />
              </div>
              <div className="text-left flex-1 min-w-0">
                <div className="text-[14px] font-medium" style={{ color: 'var(--ink)' }}>
                  Share in Aangan chat
                </div>
                <div className="text-[12px] truncate" style={{ color: 'var(--ink-soft)' }}>
                  Send the post link to someone you chat with
                </div>
              </div>
            </button>
            <button onClick={handleShareOutside}
                    className="w-full flex items-center gap-3 p-3 rounded-2xl active:scale-[0.99] transition-transform mb-2"
                    style={{ background: 'var(--surface)', border: '1px solid var(--border)' }}>
              <div className="w-10 h-10 rounded-full flex items-center justify-center shrink-0"
                   style={{ background: 'var(--accent-soft)' }}>
                <ExternalLink size={18} style={{ color: 'var(--accent)' }} />
              </div>
              <div className="text-left flex-1 min-w-0">
                <div className="text-[14px] font-medium" style={{ color: 'var(--ink)' }}>
                  Share outside Aangan
                </div>
                <div className="text-[12px] truncate" style={{ color: 'var(--ink-soft)' }}>
                  WhatsApp, Instagram, or any app
                </div>
              </div>
            </button>
            <button onClick={handleCopyLink}
                    className="w-full flex items-center gap-3 p-3 rounded-2xl active:scale-[0.99] transition-transform"
                    style={{ background: 'var(--surface)', border: '1px solid var(--border)' }}>
              <div className="w-10 h-10 rounded-full flex items-center justify-center shrink-0"
                   style={{ background: 'var(--accent-soft)' }}>
                <LinkIcon size={18} style={{ color: 'var(--accent)' }} />
              </div>
              <div className="text-left flex-1 min-w-0">
                <div className="text-[14px] font-medium" style={{ color: 'var(--ink)' }}>
                  Copy link
                </div>
                <div className="text-[12px] truncate" style={{ color: 'var(--ink-soft)' }}>
                  {shareUrl}
                </div>
              </div>
            </button>
          </div>
        )}

        {mode === 'chat-picker' && (
          <div className="flex-1 overflow-y-auto px-4 pb-5">
            {chats === null && (
              <div className="flex flex-col items-center justify-center py-10">
                <div className="w-8 h-8 rounded-full border-2 animate-spin"
                     style={{ borderColor: 'var(--border)', borderTopColor: 'var(--accent)' }} />
                <p className="text-[12px] mt-2" style={{ color: 'var(--ink-soft)' }}>Loading your chats…</p>
              </div>
            )}
            {chats !== null && chats.length === 0 && (
              <div className="text-center py-10">
                <p className="text-[14px]" style={{ color: 'var(--ink)' }}>No chats yet</p>
                <p className="text-[12px] mt-1" style={{ color: 'var(--ink-soft)' }}>
                  Start a chat from a batchmate's profile, then share posts here.
                </p>
                <button onClick={() => setMode('menu')}
                        className="mt-4 px-4 py-2 rounded-full text-[13px] font-medium"
                        style={{ background: 'var(--ink)', color: '#fff' }}>
                  Back
                </button>
              </div>
            )}
            {chats !== null && chats.length > 0 && (
              <>
                <button onClick={() => setMode('menu')}
                        className="text-[12px] mb-3 px-2 py-1"
                        style={{ color: 'var(--ink-soft)' }}>
                  ← Back to options
                </button>
                {chats.map(chat => {
                  const partnerId = (chat.participants || []).find(uid => uid !== currentUser.id);
                  const partner = chatPartners[partnerId];
                  const partnerName = partner?.name || 'Unknown';
                  const partnerPhoto = partner?.photoUrl;
                  const isSending = sendingTo === chat.id;
                  return (
                    <button key={chat.id}
                            onClick={() => handleSendToChat(chat)}
                            disabled={isSending}
                            className="w-full flex items-center gap-3 p-3 rounded-2xl active:scale-[0.99] transition-transform mb-2"
                            style={{
                              background: 'var(--surface)',
                              border: '1px solid var(--border)',
                              opacity: isSending ? 0.6 : 1,
                            }}>
                      <div className="w-10 h-10 rounded-full flex items-center justify-center shrink-0 overflow-hidden"
                           style={{ background: 'var(--accent-soft)' }}>
                        {partnerPhoto ? (
                          <img src={partnerPhoto} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                        ) : (
                          <span className="text-[14px] font-medium" style={{ color: 'var(--accent)' }}>
                            {partnerName.charAt(0).toUpperCase()}
                          </span>
                        )}
                      </div>
                      <div className="text-left flex-1 min-w-0">
                        <div className="text-[14px] font-medium truncate" style={{ color: 'var(--ink)' }}>
                          {partnerName}
                        </div>
                        {chat.lastMessage && (
                          <div className="text-[12px] truncate" style={{ color: 'var(--ink-soft)' }}>
                            {chat.lastMessage}
                          </div>
                        )}
                      </div>
                      {isSending ? (
                        <div className="w-4 h-4 rounded-full border-2 animate-spin shrink-0"
                             style={{ borderColor: 'var(--border)', borderTopColor: 'var(--accent)' }} />
                      ) : (
                        <Send size={16} style={{ color: 'var(--ink-soft)' }} />
                      )}
                    </button>
                  );
                })}
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
